import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getEventUsers, getAllEvents, getSessions, getDevices } from '../services/api';
import { useApiQuery } from '../hooks/useApiQuery';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  ArrowLeftIcon,
  CalendarIcon,
  UserGroupIcon,
  CheckCircleIcon,
  QrCodeIcon,
  ClockIcon,
  ArrowRightOnRectangleIcon,
  DevicePhoneMobileIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

const COLORS = ['#1672b9', '#2a85ce', '#3f98e3', '#6bb5f5', '#9acdff'];

const EventDashboard = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  // ✅ React Query: Fetch event users
  const {
    data: users = [],
    isLoading: usersLoading,
  } = useApiQuery(
    ['event-users', eventId],
    () => getEventUsers(eventId).then(res => res.data),
    {
      enabled: !!eventId,
      staleTime: 5 * 60 * 1000,
      onError: () => console.error('Failed to fetch event users'),
    }
  );

  // ✅ React Query: Fetch all events
  const {
    data: allEvents = [],
    isLoading: eventsLoading,
  } = useApiQuery(
    ['all-events'],
    () => getAllEvents().then(res => res.data),
    {
      staleTime: 5 * 60 * 1000,
      onError: () => console.error('Failed to fetch events'),
    }
  );

  // ✅ React Query: Fetch sessions
  const {
    data: allSessions = [],
    isLoading: sessionsLoading,
  } = useApiQuery(
    ['sessions'],
    () => getSessions({}).then(res => res.data),
    {
      staleTime: 30 * 1000,
      onError: () => console.error('Failed to fetch sessions'),
    }
  );

  // ✅ React Query: Fetch devices
  const {
    data: allDevices = [],
    isLoading: devicesLoading,
  } = useApiQuery(
    ['devices'],
    () => getDevices().then(res => res.data),
    {
      staleTime: 5 * 60 * 1000,
      onError: () => console.error('Failed to fetch devices'),
    }
  );

  const isLoading = usersLoading || eventsLoading || sessionsLoading || devicesLoading;

  // ✅ Memoized: Find event
  const event = useMemo(() => {
    return allEvents.find(e => e._id === eventId);
  }, [allEvents, eventId]);

  // ✅ Memoized: Filter sessions for this event
  const eventSessions = useMemo(() => {
    return allSessions.filter(s => s.eventId?._id === eventId || s.eventId === eventId);
  }, [allSessions, eventId]);

  // ✅ Memoized: Filter devices for this event
  const eventDevices = useMemo(() => {
    return allDevices.filter(d => d.eventId?._id === eventId || d.eventId === eventId);
  }, [allDevices, eventId]);

  // ✅ Memoized: Apply date filter
  const filteredSessions = useMemo(() => {
    if (!dateRange.start && !dateRange.end) return eventSessions;

    return eventSessions.filter(s => {
      const entryDate = new Date(s.entryTime);
      if (dateRange.start && entryDate < new Date(dateRange.start)) return false;
      if (dateRange.end && entryDate > new Date(dateRange.end)) return false;
      return true;
    });
  }, [eventSessions, dateRange]);

  // ✅ Memoized: All computed metrics
  const metrics = useMemo(() => {
    const sessionsList = filteredSessions;
    const usersList = users;
    const devicesList = eventDevices;

    // Summary
    const today = new Date().toISOString().split('T')[0];
    const presentToday = sessionsList.filter(s =>
      s.status === 'IN' && new Date(s.entryTime).toISOString().split('T')[0] === today
    ).length;
    const totalScans = sessionsList.length;
    const completed = sessionsList.filter(s => s.status === 'OUT');
    const avgTime = completed.length
      ? completed.reduce((sum, s) => {
        const duration = (new Date(s.exitTime) - new Date(s.entryTime)) / 1000 / 60;
        return sum + duration;
      }, 0) / completed.length
      : 0;
    const currentlyInside = sessionsList.filter(s => s.status === 'IN').length;

    const summary = {
      totalUsers: usersList.length,
      presentToday,
      totalScans,
      avgTimeSpent: avgTime.toFixed(1),
      currentlyInside
    };

    // Trend data
    const hours = Array(24).fill(0).map((_, i) => ({ hour: i, in: 0, out: 0 }));
    sessionsList.forEach(s => {
      const hour = new Date(s.entryTime).getHours();
      if (s.status === 'IN') hours[hour].in++;
      else if (s.status === 'OUT') hours[hour].out++;
    });
    const trendData = hours;

    // User distribution
    const userStatus = {};
    usersList.forEach(u => { userStatus[u._id] = { in: false, out: false }; });
    sessionsList.forEach(s => {
      const userId = s.userId?._id || s.userId;
      if (userId && userStatus[userId]) {
        if (s.status === 'IN') userStatus[userId].in = true;
        else if (s.status === 'OUT') userStatus[userId].out = true;
      }
    });
    let present = 0, absent = 0, currentlyInsideCount = 0, completedCount = 0;
    Object.values(userStatus).forEach(status => {
      if (status.in && status.out) completedCount++;
      else if (status.in && !status.out) currentlyInsideCount++;
      else if (!status.in && !status.out) absent++;
      if (status.in) present++;
    });
    const userDistribution = [
      { name: 'Present (IN done)', value: present },
      { name: 'Absent (never scanned)', value: absent },
      { name: 'Currently Inside', value: currentlyInsideCount },
      { name: 'Completed (IN+OUT)', value: completedCount }
    ];

    // Avg time per user
    const userTimes = {};
    sessionsList.forEach(s => {
      const userId = s.userId?._id || s.userId;
      if (s.status === 'IN') userTimes[userId] = { inTime: new Date(s.entryTime), outTime: null };
      else if (s.status === 'OUT' && userTimes[userId]) userTimes[userId].outTime = new Date(s.entryTime);
    });
    const avgList = [];
    for (const userId in userTimes) {
      const user = usersList.find(u => u._id === userId);
      if (user && userTimes[userId].inTime && userTimes[userId].outTime) {
        const duration = (userTimes[userId].outTime - userTimes[userId].inTime) / 1000 / 60;
        avgList.push({ name: user.name, minutes: duration });
      }
    }
    avgList.sort((a, b) => b.minutes - a.minutes);
    const avgTimeData = avgList.slice(0, 10);

    // Device usage
    const deviceScans = {};
    sessionsList.forEach(s => {
      const devId = s.entryDevice?._id || s.entryDevice;
      if (devId) deviceScans[devId] = (deviceScans[devId] || 0) + 1;
    });
    const usage = [];
    for (const devId in deviceScans) {
      const device = devicesList.find(d => d._id === devId);
      if (device) usage.push({ name: device.name, scans: deviceScans[devId] });
    }
    usage.sort((a, b) => b.scans - a.scans);
    const deviceUsage = usage;

    // IN/OUT ratio
    let inCount = 0, outCount = 0;
    sessionsList.forEach(s => {
      if (s.status === 'IN') inCount++;
      else if (s.status === 'OUT') outCount++;
    });
    const inOutRatio = { in: inCount, out: outCount };

    // Peak hours
    const peakHoursArray = Array(24).fill(0);
    sessionsList.forEach(s => {
      const hour = new Date(s.entryTime).getHours();
      peakHoursArray[hour]++;
    });
    const peakHours = peakHoursArray.map((count, hour) => ({ hour, count }));

    return {
      summary,
      trendData,
      userDistribution,
      avgTimeData,
      deviceUsage,
      inOutRatio,
      peakHours
    };
  }, [filteredSessions, users, eventDevices]);

  // ✅ Reset date filter
  const resetDateFilter = useCallback(() => {
    setDateRange({ start: '', end: '' });
  }, []);

  // ✅ Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3"></div>
          <p className="text-gray-500 text-sm">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const tooltipStyle = {
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    fontSize: '12px',
    color: '#111827',
    padding: '8px 12px',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/events-manager')}
            className="p-2 rounded-md text-gray-500 hover:text-[#1672b9] hover:bg-gray-50 transition-colors"
            aria-label="Go back"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Event Dashboard: {event?.name}</h1>
            <p className="text-sm text-gray-500 mt-1">
              Real-time analytics and attendance insights
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 bg-white p-2 rounded-lg border border-gray-100 shadow-sm">
          <CalendarIcon className="w-5 h-5 text-gray-400" />
          <input
            type="date"
            value={dateRange.start}
            onChange={e => setDateRange({ ...dateRange, start: e.target.value })}
            className="border border-gray-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9]"
          />
          <span className="text-gray-400">—</span>
          <input
            type="date"
            value={dateRange.end}
            onChange={e => setDateRange({ ...dateRange, end: e.target.value })}
            className="border border-gray-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9]"
          />
          <button
            onClick={resetDateFilter}
            className="text-sm text-[#1672b9] hover:underline"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
        <SummaryCard title="Total Users" value={metrics.summary.totalUsers} icon={<UserGroupIcon className="w-5 h-5" />} />
        <SummaryCard title="Present Today" value={metrics.summary.presentToday} icon={<CheckCircleIcon className="w-5 h-5" />} />
        <SummaryCard title="Total Scans" value={metrics.summary.totalScans} icon={<QrCodeIcon className="w-5 h-5" />} />
        <SummaryCard title="Avg Time (min)" value={metrics.summary.avgTimeSpent} icon={<ClockIcon className="w-5 h-5" />} />
        <SummaryCard title="Currently Inside" value={metrics.summary.currentlyInside} icon={<ArrowRightOnRectangleIcon className="w-5 h-5" />} />
      </div>

      {/* Row 1: Entry/Exit Trend + User Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Entry vs Exit Trend (Hourly)">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={metrics.trendData}>
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="hour" tick={{ fill: '#6b7280', fontSize: 12 }} axisLine={{ stroke: '#e5e7eb' }} />
              <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} axisLine={{ stroke: '#e5e7eb' }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="in" stroke="#1672b9" name="IN" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="out" stroke="#9ca3af" name="OUT" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="User Distribution">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={metrics.userDistribution} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
                {metrics.userDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Row 2: Avg Time Spent + Device Usage */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Average Time Spent (Top 10 Users)">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={metrics.avgTimeData} layout="vertical">
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 12 }} axisLine={{ stroke: '#e5e7eb' }} />
              <YAxis dataKey="name" type="category" width={100} tick={{ fill: '#6b7280', fontSize: 12 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="minutes" fill="#1672b9" name="Minutes" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Device Usage (Scans per Device)">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={metrics.deviceUsage}>
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: '#6b7280', fontSize: 12 }} axisLine={{ stroke: '#e5e7eb' }} />
              <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} axisLine={{ stroke: '#e5e7eb' }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="scans" fill="#1672b9" name="Scans" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Row 3: IN/OUT Ratio + Peak Hours */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="IN vs OUT Ratio">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={[{ name: 'IN', value: metrics.inOutRatio.in }, { name: 'OUT', value: metrics.inOutRatio.out }]}
                dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={100} label>
                <Cell fill="#1672b9" />
                <Cell fill="#9ca3af" />
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Peak Hours Heatmap">
          <div className="grid grid-cols-12 gap-1.5">
            {metrics.peakHours.map((item, idx) => (
              <div key={idx} className="text-center">
                <div
                  className="h-14 w-full rounded-md flex items-center justify-center text-white font-semibold text-sm transition-all hover:scale-105"
                  style={{
                    backgroundColor: `rgba(22, 114, 185, ${Math.min(0.85, item.count / 15)})`
                  }}
                >
                  {item.count}
                </div>
                <div className="text-[10px] mt-1 text-gray-500">{idx}:00</div>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>
    </div>
  );
};

// ✅ Memoized Summary Card
const SummaryCard = React.memo(({ title, value, icon }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 transition-all hover:shadow-md">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">{title}</p>
        <p className="text-2xl font-semibold text-[#1672b9] mt-1">{value}</p>
      </div>
      <div className="p-2 rounded-full bg-[#1672b9]/10 text-[#1672b9]">{icon}</div>
    </div>
  </div>
));
SummaryCard.displayName = 'SummaryCard';

// ✅ Memoized Chart Card
const ChartCard = React.memo(({ title, children }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
    <h3 className="text-base font-medium text-gray-900 mb-4">{title}</h3>
    {children}
  </div>
));
ChartCard.displayName = 'ChartCard';

export default EventDashboard;